import type { InterstitialImageOrGraph } from '@/api/generated-catch-backend/model/interstitialImageOrGraph';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { InterstitialVideoDrawer as InterstitialVideoDrawerType } from '../../../types';
import {
  BackgroundAsset,
  FRAME_TRANSITION_DURATION,
  GraphicLottie,
  InterstitialTextContentBlock
} from './interstitial-shared';

type InterstitialVideoDrawerProps = {
  interstitial: InterstitialVideoDrawerType;
  onNext?: () => void;
};

// Determine viewport size tier based on SMALLEST constraint
type SizeTier = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const calculateViewportSizeTier = (width: number, height: number): SizeTier => {
  // Map width to tier
  let widthTier: SizeTier;
  if (width < 380) widthTier = 'xs';
  else if (width < 640) widthTier = 'sm';
  else if (width < 768) widthTier = 'md';
  else if (width < 1024) widthTier = 'lg';
  else widthTier = 'xl';

  // Map height to tier (shifted down for more appropriate breakpoints)
  let heightTier: SizeTier;
  if (height < 400) heightTier = 'xs';  // Very constrained (was never reached before)
  else if (height < 500) heightTier = 'sm';  // Was xs
  else if (height < 600) heightTier = 'md';  // Was sm
  else if (height < 700) heightTier = 'lg';  // Was md
  else heightTier = 'xl';  // >= 700px

  // Return the SMALLER tier (most constraining)
  const tiers: SizeTier[] = ['xs', 'sm', 'md', 'lg', 'xl'];
  const widthIndex = tiers.indexOf(widthTier);
  const heightIndex = tiers.indexOf(heightTier);
  return tiers[Math.min(widthIndex, heightIndex)];
};

// Custom hook to track viewport size tier with resize handling
const useViewportSizeTier = (): SizeTier => {
  const [sizeTier, setSizeTier] = useState<SizeTier>(() => {
    if (typeof window === 'undefined') return 'md';
    return calculateViewportSizeTier(window.innerWidth, window.innerHeight);
  });

  useEffect(() => {
    const handleResize = () => {
      const newTier = calculateViewportSizeTier(window.innerWidth, window.innerHeight);
      setSizeTier(newTier);
    };

    // Debounce resize events
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return sizeTier;
};

// Calculate responsive image dimensions based on tier and viewport
const calculateImageDimensions = (
  location: 'main' | 'bottom',
  tier: SizeTier,
  viewportHeight: number
): React.CSSProperties => {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 400;

  // Define drawer overlap amounts in pixels
  const drawerOverlaps = {
    xs: 32,   // 2rem
    sm: 40,   // 2.5rem
    md: 48,   // 3rem
    lg: 48,   // 3rem
    xl: 48    // 3rem
  };

  // For main graphic, account for drawer overlap
  const effectiveHeight = location === 'main'
    ? viewportHeight * 0.5 - (drawerOverlaps[tier as keyof typeof drawerOverlaps] || 48)
    : viewportHeight;

  // Define base height percentages for each tier
  const heightConfigs = {
    xs: 0.20,
    sm: 0.25,
    md: 0.30,
    lg: 0.35,
    xl: 0.40
  };

  // Calculate width based on viewport percentage for small screens, fixed for larger
  let maxWidth;
  if (tier === 'xs') {
    // 90% of viewport width, capped at 400px
    maxWidth = Math.min(viewportWidth * 0.90, 400);
  } else if (tier === 'sm') {
    // 85% of viewport width, capped at 500px
    maxWidth = Math.min(viewportWidth * 0.85, 500);
  } else if (tier === 'md') {
    // 75% of viewport width, capped at 550px
    maxWidth = Math.min(viewportWidth * 0.75, 550);
  } else {
    // Larger screens use different sizing based on location
    if (location === 'bottom') {
      // Bottom images should be smaller on large screens
      const bottomFixedWidths: Record<string, number> = { lg: 400, xl: 450 };
      maxWidth = bottomFixedWidths[tier] || 450;
    } else {
      // Main graphics can be larger
      const mainFixedWidths: Record<string, number> = { lg: 550, xl: 700 };
      maxWidth = mainFixedWidths[tier] || 700;
    }
  }

  // Calculate height based on location
  let finalMaxHeight;
  if (location === 'bottom') {
    // For bottom images, use generous height limits to avoid constraining width
    const tierHeightLimits = {
      xs: 0.50,  // 50% of viewport height for xs
      sm: 0.48,  // 48% for sm
      md: 0.45,  // 45% for md and above
      lg: 0.45,
      xl: 0.45
    };
    finalMaxHeight = viewportHeight * (tierHeightLimits[tier as keyof typeof tierHeightLimits] || 0.45);
  } else {
    // Main graphic uses effective height (half viewport minus drawer overlap)
    // For xs tier, use much more of the available space
    if (tier === 'xs') {
      finalMaxHeight = effectiveHeight * 0.70; // Use 70% of available space on xs
    } else if (tier === 'sm') {
      finalMaxHeight = effectiveHeight * 0.50; // Use 50% on sm
    } else {
      // Larger screens use conservative sizing
      const baseHeightPercent = heightConfigs[tier];
      finalMaxHeight = effectiveHeight * baseHeightPercent * 1.6;
    }
  }


  return {
    maxHeight: `${finalMaxHeight}px`,
    maxWidth: `${maxWidth}px`,
    width: '100%',
    height: 'auto',
    objectFit: 'contain'
  };
};

// Custom hook for responsive image dimensions
const useResponsiveImageDimensions = (location: 'main' | 'bottom') => {
  const sizeTier = useViewportSizeTier();
  const [dimensions, setDimensions] = useState<React.CSSProperties>(() => {
    if (typeof window === 'undefined') {
      return {
        maxWidth: '400px',
        width: '100%',
        height: 'auto',
        objectFit: 'contain'
      };
    }
    return calculateImageDimensions(location, sizeTier, window.innerHeight);
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions(calculateImageDimensions(location, sizeTier, window.innerHeight));
    };

    // Update on tier change or resize
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [location, sizeTier]);

  return dimensions;
};

// Helper component to render the main graphic
const MainGraphic = ({ imageOrGraph }: { imageOrGraph: InterstitialImageOrGraph | null | undefined }) => {
  const dimensions = useResponsiveImageDimensions('main');
  
  if (!imageOrGraph) return null;

  const isLottie = imageOrGraph.type === 'lottie';

  if (isLottie) {
    // For Lottie, use width/height instead of maxWidth/maxHeight for better control
    const lottieDimensions = {
      width: dimensions.maxWidth,
      height: dimensions.maxHeight,
      objectFit: dimensions.objectFit
    };

    return (
      <div className="flex w-full justify-center">
        <div
          className="w-full rounded-lg flex justify-center items-center"
          style={{
            ...dimensions,
            overflow: 'visible'
          }}
          data-content-type="lottie"
        >
          <GraphicLottie
            srcUrl={imageOrGraph.asset}
            loop
            style={lottieDimensions}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full justify-center">
      <img
        src={imageOrGraph.asset}
        alt="Interstitial graphic"
        className="h-auto rounded-lg object-contain"
        style={dimensions}
        data-content-type="image"
      />
    </div>
  );
};

export const InterstitialVideoDrawer = ({ interstitial, onNext }: InterstitialVideoDrawerProps) => {
  const backgroundOpacity = interstitial.backgroundOpacity ?? 0.7;
  const sizeTier = useViewportSizeTier();
  const bottomImageDimensions = useResponsiveImageDimensions('bottom');

  // Responsive padding based on tier
  const isConstrained = sizeTier === 'xs' || sizeTier === 'sm';
  const isMedium = sizeTier === 'md';


  return (
    <motion.div
      className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-black font-['Inter']"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: FRAME_TRANSITION_DURATION }}
    >

      {/* Video Section - Top Half */}
      <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden">
        {/* Background asset (video or image) */}
        <BackgroundAsset
          backgroundAsset={interstitial.backgroundAsset}
          classNamePrefix="interstitial-video-drawer"
          className="absolute inset-0 z-[1] h-full w-full bg-cover bg-center bg-no-repeat [&>video]:object-cover [&>video]:h-full [&>video]:w-full"
        />

        {/* Background overlay for opacity control */}
        <div
          className="absolute inset-0 z-[2]"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${backgroundOpacity})`,
          }}
        />

        {/* Content over video - centered with drawer overlap padding */}
        <div className={`relative z-[3] flex h-full w-full flex-col items-center justify-center ${
          isConstrained ? 'px-3 pb-12' : isMedium ? 'px-4 pb-16' : 'px-6 pb-20 [@media(min-height:700px)]:pb-24'
        }`}>
          {/* Text above graphic - only add padding if it exists */}
          {(interstitial.titleAbove || interstitial.subtitleAbove) && (
            <InterstitialTextContentBlock
              title={interstitial.titleAbove}
              subtitle={interstitial.subtitleAbove}
              containerClassName={isConstrained ? 'pb-1' : isMedium ? 'pb-2' : 'pb-3 [@media(min-height:700px)]:pb-6'}
              textColorClass="text-white"
            />
          )}

          {/* Main graphic */}
          {interstitial.imageOrGraph && (
            <div className="flex w-full justify-center">
              <MainGraphic imageOrGraph={interstitial.imageOrGraph} />
            </div>
          )}
        </div>
      </div>

      {/* White Drawer Section - Bottom */}
      <div className={`relative z-10 flex w-full flex-col items-center bg-gradient-to-b from-white to-[#F5F5F7] rounded-t-[2rem] ${
        isConstrained ? 'mt-[-2rem] pt-6 pb-safe-offset-2' :
        isMedium ? 'mt-[-2.5rem] pt-6 pb-safe-offset-3' :
        'mt-[-3rem] pt-6 [@media(min-height:700px)]:pt-8 pb-safe-offset-4 [@media(min-height:700px)]:pb-safe-offset-8'
      }`}>
        <div className="flex w-full flex-col items-center">
          {/* Text below graphic */}
          <div className={`flex w-full flex-col items-center ${
            isConstrained ? 'px-4' : isMedium ? 'px-4 md:px-8' : 'px-6 md:px-12'
          }`}>
            <InterstitialTextContentBlock
              title={interstitial.titleBelow}
              subtitle={interstitial.subtitleBelow}
              containerClassName={isConstrained ? 'pb-1' : isMedium ? 'pb-2' : 'pb-3 [@media(min-height:700px)]:pb-6'}
              textColorClass="text-gray-900"
            />
          </div>

          {/* Image below */}
          {interstitial.imageOrGraphBelow && (
            <div
              className={`flex w-full justify-center ${
                isConstrained ? 'px-2 pb-1' : isMedium ? 'px-3 pb-2 sm:px-4' : 'px-2 pb-3 [@media(min-height:700px)]:pb-6 sm:px-4 md:px-8'
              }`}
            >
              {interstitial.imageOrGraphBelow.type === 'lottie' ? (
                <div
                  className="rounded-lg w-full"
                  style={bottomImageDimensions}
                  data-content-type="lottie"
                >
                  <GraphicLottie srcUrl={interstitial.imageOrGraphBelow.asset} loop />
                </div>
              ) : (
                <img
                  src={interstitial.imageOrGraphBelow.asset}
                  alt=""
                  className="h-auto rounded-lg w-full object-contain"
                  style={bottomImageDimensions}
                  data-content-type="image"
                />
              )}
            </div>
          )}

          {/* Text content with normal padding */}
          <div className={`flex w-full flex-col items-center ${
            isConstrained ? 'px-3' : isMedium ? 'px-4 md:px-8' : 'px-6 md:px-12'
          }`}>

            {/* Continue button */}
            {onNext && (
              <div className={`flex w-full justify-center ${
                isConstrained ? 'pb-1' : isMedium ? 'pb-2' : 'pb-2 [@media(min-height:700px)]:pb-4'
              }`}>
                <button
                  onClick={onNext}
                  className={`flex w-full max-w-[20rem] items-center justify-center rounded-full bg-[#9EFFC1] ${
                    isConstrained ? 'px-6 py-2.5' : isMedium ? 'px-7 py-3' : 'px-8 py-3 [@media(min-height:700px)]:py-4'
                  } text-lg font-semibold text-[#0F2837] transition-all hover:bg-[#8AEDB0] active:scale-95 md:max-w-[24rem]`}
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
